import type { FeatureCollection, Feature, LineString, Point } from 'geojson';
import distance from '@turf/distance';
import bearing from '@turf/bearing';
import destination from '@turf/destination';
import lineIntersect from '@turf/line-intersect';
import { point, lineString } from '@turf/helpers';

interface Fix {
    latitude: number;
    longitude: number;
    altitude?: number;
    fix_name?: string;
    fix_function?: string;
    fix_type?: string;
    course?: number;
    magnetic_variation_west?: number;
    turn_direction?: 'left' | 'right';
    arc_center_latitude?: number;
    arc_center_longitude?: number;
    arc_radius?: number;
}

interface Approach {
    fixes: Fix[];
}

interface Transition {
    fixes: Fix[];
    transition_identifier: string;
}

interface IAPPayload {
    approachName: string;
    mainApproach: Approach;
    transitions: Transition[];
}

/**
 * Calculate bearing between two points using turf.js
 */
function calculateBearing(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
): number {
    const from = point([lon1, lat1]);
    const to = point([lon2, lat2]);
    const bearingDegrees = bearing(from, to);
    // Convert to 0-360 range (turf returns -180 to 180)
    return (bearingDegrees + 360) % 360;
}

/**
 * Calculate distance between two points in nautical miles using turf.js
 */
function calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
): number {
    const from = point([lon1, lat1]);
    const to = point([lon2, lat2]);
    return distance(from, to, { units: 'nauticalmiles' });
}

/**
 * Calculate a destination point given a start point, bearing, and distance
 * Uses turf.js for accurate spherical geometry calculations
 * @param lat Starting latitude in degrees
 * @param lon Starting longitude in degrees
 * @param bearing Bearing in degrees (0-360, clockwise from north)
 * @param distanceNm Distance in nautical miles
 * @returns [longitude, latitude] of destination point
 */
function calculateDestinationPoint(
    lat: number,
    lon: number,
    bearing: number,
    distanceNm: number
): [number, number] {
    const from = point([lon, lat]);
    const dest = destination(from, distanceNm, bearing, {
        units: 'nauticalmiles',
    });
    return dest.geometry.coordinates as [number, number];
}

/**
 * Calculate the center of an arc given two points on the arc and the radius
 * @param startLat Starting point latitude
 * @param startLon Starting point longitude
 * @param endLat Ending point latitude
 * @param endLon Ending point longitude
 * @param radiusNm Arc radius in nautical miles
 * @param turnDirection 'left' or 'right' turn direction
 * @returns [longitude, latitude] of the arc center
 */
function calculateArcCenter(
    startLat: number,
    startLon: number,
    endLat: number,
    endLon: number,
    radiusNm: number,
    turnDirection: string
): [number, number] {
    // Find midpoint between start and end
    const midLat = (startLat + endLat) / 2;
    const midLon = (startLon + endLon) / 2;

    // Calculate distance between start and end points
    const d = calculateDistance(startLat, startLon, endLat, endLon);

    // Calculate distance from midpoint to center using Pythagorean theorem
    // If d > 2*radius, the arc is impossible - use radius as fallback
    const halfD = d / 2;
    if (halfD > radiusNm) {
        console.warn(
            `DME arc: distance between points (${d} NM) exceeds diameter (${2 * radiusNm} NM)`
        );
        // Return midpoint as fallback
        return [midLon, midLat];
    }

    const distToCenter = Math.sqrt(radiusNm * radiusNm - halfD * halfD);

    // Calculate bearing from start to end
    const trackBearing = calculateBearing(startLat, startLon, endLat, endLon);

    // Perpendicular bearing depends on turn direction
    // For a right turn, center is 90° to the right of track
    // For a left turn, center is 90° to the left of track
    const perpendicularBearing =
        turnDirection === 'right' ? trackBearing + 90 : trackBearing - 90;

    // Calculate center point
    const center = calculateDestinationPoint(
        midLat,
        midLon,
        perpendicularBearing,
        distToCenter
    );

    return center;
}

/**
 * Generate points along an arc
 */
function generateArcPoints(
    centerLat: number,
    centerLon: number,
    radiusNm: number,
    startLat: number,
    startLon: number,
    endLat: number,
    endLon: number,
    turnDirection: string,
    numPoints: number = 50
): [number, number][] {
    const points: [number, number][] = [];

    // Calculate start and end angles
    const startAngle = calculateBearing(
        centerLat,
        centerLon,
        startLat,
        startLon
    );
    const endAngle = calculateBearing(centerLat, centerLon, endLat, endLon);

    // Calculate sweep angle based on turn direction
    let sweepAngle: number;
    if (turnDirection === 'right') {
        sweepAngle =
            endAngle >= startAngle
                ? endAngle - startAngle
                : 360 - startAngle + endAngle;
    } else {
        sweepAngle =
            endAngle <= startAngle
                ? startAngle - endAngle
                : startAngle + 360 - endAngle;
        sweepAngle = -sweepAngle;
    }

    // Generate points along the arc
    for (let i = 0; i <= numPoints; i++) {
        const fraction = i / numPoints;
        const bearing = startAngle + sweepAngle * fraction;

        // Calculate point on arc using proper spherical geometry
        const point = calculateDestinationPoint(
            centerLat,
            centerLon,
            bearing,
            radiusNm
        );
        points.push(point);
    }

    return points;
}

/**
 * Generate a complete procedure turn path
 * @param startLat Starting latitude (the pturn fix)
 * @param startLon Starting longitude (the pturn fix)
 * @param course The inbound course in degrees
 * @param turnDirection 'left' or 'right' for the initial 45° turn
 * @param magneticVariation Magnetic variation (east positive, west negative)
 * @param speed Aircraft speed in knots (default 140kt)
 * @returns Object with path coordinates and optional debug lines
 */
function generateProcedureTurnPath(
    startLat: number,
    startLon: number,
    course: number,
    turnDirection: 'left' | 'right',
    magneticVariation: number = 0,
    speed: number = 90
): {
    path: [number, number][];
    debugLines?: {
        inboundLine: [number, number][];
        approachLine: [number, number][];
    };
} {
    const points: [number, number][] = [];

    // Convert magnetic course to true course
    const trueCourse = course - magneticVariation;

    // Constants
    const straightTimeSeconds = 60;
    const straightDistance = speed * (straightTimeSeconds / 3600); // Convert to hours: 2.333 NM
    const turnRadius = straightDistance / Math.PI; // R = VT/π ≈ 0.743 NM

    // Add starting point (the pturn fix)
    points.push([startLon, startLat]);

    // Step 1: The course field already represents the outbound heading (45° turn already applied)
    const outboundHeading = trueCourse;

    // Step 2: Fly straight outbound for 60 seconds
    const outboundEnd = calculateDestinationPoint(
        startLat,
        startLon,
        outboundHeading,
        straightDistance
    );
    points.push(outboundEnd);

    // Step 3: 180° turn (same direction as initial turn)
    const turnDirection180 = turnDirection;

    // Calculate center of the turn circle
    // The center is perpendicular to the current heading at distance = radius
    const centerBearing =
        outboundHeading + (turnDirection180 === 'right' ? 90 : -90);
    const turnCenter = calculateDestinationPoint(
        outboundEnd[1], // lat
        outboundEnd[0], // lon
        centerBearing,
        turnRadius
    );

    // Generate arc points for the 180° turn
    const numArcPoints = 36; // 5° increments for smooth arc
    const startAngle = calculateBearing(
        turnCenter[1],
        turnCenter[0],
        outboundEnd[1],
        outboundEnd[0]
    );

    for (let i = 1; i <= numArcPoints; i++) {
        const angleDelta = (180 / numArcPoints) * i;
        const bearing =
            startAngle +
            (turnDirection180 === 'right' ? angleDelta : -angleDelta);
        const arcPoint = calculateDestinationPoint(
            turnCenter[1],
            turnCenter[0],
            bearing,
            turnRadius
        );
        points.push(arcPoint);
    }

    // Step 4: Fly inbound until intercepting the final approach course
    const inboundHeading = (outboundHeading + 180) % 360;
    const turnEndPoint = points[points.length - 1];

    // Create extended line for inbound track (100 NM should be long enough)
    const inboundFarPoint = calculateDestinationPoint(
        turnEndPoint[1],
        turnEndPoint[0],
        inboundHeading,
        10
    );
    const inboundLine = lineString([turnEndPoint, inboundFarPoint]);

    // Calculate the final approach course (reciprocal of the 45° turn outbound heading)
    // This is the original inbound course before the 45° turn
    const finalApproachCourse = (outboundHeading - 45) % 360;

    // Create extended line for final approach course (100 NM in both directions)
    const approachStartPoint = calculateDestinationPoint(
        startLat,
        startLon,
        (finalApproachCourse + 180) % 360,
        10
    );
    const approachEndPoint = calculateDestinationPoint(
        startLat,
        startLon,
        finalApproachCourse,
        10
    );
    const approachLine = lineString([approachStartPoint, approachEndPoint]);

    // Find intersection
    const intersections = lineIntersect(inboundLine, approachLine);

    if (intersections.features.length > 0) {
        // Use the first intersection point
        const intersectionPoint = intersections.features[0].geometry
            .coordinates as [number, number];
        points.push(intersectionPoint);
    } else {
        // Fallback: just fly straight for the same distance
        const fallbackEnd = calculateDestinationPoint(
            turnEndPoint[1],
            turnEndPoint[0],
            inboundHeading,
            straightDistance
        );
        points.push(fallbackEnd);
    }

    // Return to starting point to complete the procedure turn
    points.push([startLon, startLat]);

    return {
        path: points,
        debugLines: {
            inboundLine: inboundLine.geometry.coordinates as [number, number][],
            approachLine: approachLine.geometry.coordinates as [
                number,
                number,
            ][],
        },
    };
}

/**
 * Convert IAP payload to GeoJSON
 */
export function iapToGeojson(payload: IAPPayload): FeatureCollection {
    const features: Feature[] = [];

    // Process main approach
    const mainApproachFeatures = processFixSequence(
        payload.mainApproach.fixes,
        'main',
        payload.approachName,
        false,
        payload.mainApproach.fixes
    );
    features.push(...mainApproachFeatures);

    // Process transitions
    payload.transitions.forEach(transition => {
        const transitionFeatures = processFixSequence(
            transition.fixes,
            'transition',
            transition.transition_identifier,
            false,
            payload.mainApproach.fixes
        );
        features.push(...transitionFeatures);
    });

    return {
        type: 'FeatureCollection',
        features,
    };
}

/**
 * Process a sequence of fixes into GeoJSON features
 */
function processFixSequence(
    fixes: Fix[],
    segmentType: 'main' | 'transition',
    identifier: string,
    debug: boolean = false,
    mainApproachFixes?: Fix[]
): Feature[] {
    const lineFeatures: Feature[] = [];
    const pointFeatures: Feature[] = [];

    for (let i = 0; i < fixes.length; i++) {
        const fix = fixes[i];

        // Skip creating points for hold pattern fixes (they have arc_center properties)
        const isHoldFix =
            fix.arc_center_latitude !== undefined &&
            fix.arc_center_longitude !== undefined &&
            fix.arc_radius !== undefined;

        // Add fix point (but not for hold pattern fixes)
        if (!isHoldFix) {
            const isMainApproach = segmentType === 'main';
            const pointFeature: Feature<Point> = {
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [fix.longitude, fix.latitude],
                },
                properties: {
                    type: 'fix',
                    segmentType,
                    identifier,
                    label: fix.fix_name,
                    fixFunction: fix.fix_function,
                    fixType: fix.fix_type,
                    altitude: fix.altitude,
                    // Color based on segment type (use transparency for non-main)
                    color: isMainApproach ? '#FF00FF' : '#FF00FF66',
                    // Add text label properties if fix has a name
                    ...(fix.fix_name && {
                        text: fix.fix_name,
                        textColor: isMainApproach ? '#ffffff' : '#ffffff99',
                        textSize: 13,
                        textHaloColor: '#000000',
                        textHaloWidth: 2,
                        textAnchor: 'top',
                        textOffset: [0, 8],
                    }),
                },
            };
            pointFeatures.push(pointFeature);
        }

        // Check if this is a DME arc
        if (fix.fix_type === 'dmearc' && i > 0) {
            const prevFix = fixes[i - 1];

            // Calculate the arc center from the two points
            const arcRadius = fix.arc_radius || prevFix.arc_radius;
            if (arcRadius !== undefined && fix.turn_direction !== undefined) {
                const [centerLon, centerLat] = calculateArcCenter(
                    prevFix.latitude,
                    prevFix.longitude,
                    fix.latitude,
                    fix.longitude,
                    arcRadius,
                    fix.turn_direction
                );

                // Generate arc points
                const arcPoints = generateArcPoints(
                    centerLat,
                    centerLon,
                    arcRadius,
                    prevFix.latitude,
                    prevFix.longitude,
                    fix.latitude,
                    fix.longitude,
                    fix.turn_direction
                );

                const arcFeature: Feature<LineString> = {
                    type: 'Feature',
                    geometry: {
                        type: 'LineString',
                        coordinates: arcPoints,
                    },
                    properties: {
                        type: 'segment',
                        segmentType,
                        identifier,
                        fixType: 'dmearc',
                        fromFix: prevFix.fix_name,
                        toFix: fix.fix_name,
                        color: segmentType === 'main' ? '#FF00FF' : '#FF00FF66',
                    },
                };
                lineFeatures.push(arcFeature);

                // Check if next fix has a course - if so, add a turn to intercept it
                if (i < fixes.length - 1) {
                    const nextFix = fixes[i + 1];
                    if (nextFix.course !== undefined) {
                        // Calculate the heading at the end of the arc (tangent to the arc)
                        const arcEndHeading = calculateBearing(
                            centerLat,
                            centerLon,
                            fix.latitude,
                            fix.longitude
                        );
                        // Tangent is perpendicular to radius
                        const tangentHeading =
                            fix.turn_direction === 'right'
                                ? (arcEndHeading + 90) % 360
                                : (arcEndHeading - 90 + 360) % 360;

                        // Convert magnetic course to true course
                        const magneticVariation =
                            fix.magnetic_variation_west || 0;
                        const targetTrueCourse =
                            nextFix.course - magneticVariation;

                        // Calculate the turn needed
                        let turnAngle = targetTrueCourse - tangentHeading;
                        if (turnAngle > 180) turnAngle -= 360;
                        if (turnAngle < -180) turnAngle += 360;

                        // Determine turn direction for the intercept turn
                        const interceptTurnDirection =
                            turnAngle > 0 ? 'right' : 'left';
                        const absTurnAngle = Math.abs(turnAngle);

                        // Create a turn if needed (more than 5 degrees)
                        if (absTurnAngle > 5) {
                            // Use a reasonable turn radius for the intercept (e.g., 2.2 NM)
                            const turnRadius = 2.2;

                            // Calculate the turn center
                            const turnCenterBearing =
                                tangentHeading +
                                (interceptTurnDirection === 'right' ? 90 : -90);
                            const [turnCenterLon, turnCenterLat] =
                                calculateDestinationPoint(
                                    fix.latitude,
                                    fix.longitude,
                                    turnCenterBearing,
                                    turnRadius
                                );

                            // Generate arc points for the turn
                            const turnStartAngle = calculateBearing(
                                turnCenterLat,
                                turnCenterLon,
                                fix.latitude,
                                fix.longitude
                            );

                            const turnPoints: [number, number][] = [];
                            const numTurnPoints = Math.ceil(absTurnAngle / 5); // 5° increments

                            for (let j = 0; j <= numTurnPoints; j++) {
                                const fraction = j / numTurnPoints;
                                const angleDelta = absTurnAngle * fraction;
                                const bearing =
                                    turnStartAngle +
                                    (interceptTurnDirection === 'right'
                                        ? angleDelta
                                        : -angleDelta);
                                const turnPoint = calculateDestinationPoint(
                                    turnCenterLat,
                                    turnCenterLon,
                                    bearing,
                                    turnRadius
                                );
                                turnPoints.push(turnPoint);
                            }

                            const turnFeature: Feature<LineString> = {
                                type: 'Feature',
                                geometry: {
                                    type: 'LineString',
                                    coordinates: turnPoints,
                                },
                                properties: {
                                    type: 'segment',
                                    segmentType,
                                    identifier,
                                    fixType: 'dmearc-intercept',
                                    fromFix: fix.fix_name,
                                    color:
                                        segmentType === 'main'
                                            ? '#FF00FF'
                                            : '#FF00FF66',
                                },
                            };
                            lineFeatures.push(turnFeature);
                        }
                    }
                }

                continue; // Skip normal segment creation
            }
        }

        // Check if this is a procedure turn
        if (fix.fix_type === 'pturn') {
            // Generate complete procedure turn path
            const result = generateProcedureTurnPath(
                fix.latitude,
                fix.longitude,
                fix.course || 0,
                fix.turn_direction || 'right',
                fix.magnetic_variation_west || 0
            );

            const lineFeature: Feature<LineString> = {
                type: 'Feature',
                geometry: {
                    type: 'LineString',
                    coordinates: result.path,
                },
                properties: {
                    type: 'segment',
                    segmentType,
                    identifier,
                    fixType: 'pturn',
                    course: fix.course,
                    fromFix: fix.fix_name,
                    color: segmentType === 'main' ? '#FF00FF' : '#FF00FF66',
                },
            };
            lineFeatures.push(lineFeature);

            // Add debug lines
            if (result.debugLines && debug) {
                const inboundDebugFeature: Feature<LineString> = {
                    type: 'Feature',
                    geometry: {
                        type: 'LineString',
                        coordinates: result.debugLines.inboundLine,
                    },
                    properties: {
                        type: 'debug',
                        debugType: 'inbound',
                        segmentType,
                        identifier,
                        fromFix: fix.fix_name,
                    },
                };
                lineFeatures.push(inboundDebugFeature);

                const approachDebugFeature: Feature<LineString> = {
                    type: 'Feature',
                    geometry: {
                        type: 'LineString',
                        coordinates: result.debugLines.approachLine,
                    },
                    properties: {
                        type: 'debug',
                        debugType: 'approach',
                        segmentType,
                        identifier,
                        fromFix: fix.fix_name,
                    },
                };
                lineFeatures.push(approachDebugFeature);
            }

            // After procedure turn, connect to the FAF in the main approach
            if (mainApproachFixes) {
                // Find the FAF (Final Approach Fix) in the main approach
                const faf = mainApproachFixes.find(
                    f => f.fix_function === 'FAF' || f.fix_function === 'faf'
                );

                if (faf) {
                    const connectingLine: Feature<LineString> = {
                        type: 'Feature',
                        geometry: {
                            type: 'LineString',
                            coordinates: [
                                [fix.longitude, fix.latitude],
                                [faf.longitude, faf.latitude],
                            ],
                        },
                        properties: {
                            type: 'segment',
                            segmentType,
                            identifier,
                            fixType: 'pturn-connect',
                            fromFix: fix.fix_name,
                            toFix: faf.fix_name,
                            color:
                                segmentType === 'main'
                                    ? '#FF00FF'
                                    : '#FF00FF66',
                        },
                    };
                    lineFeatures.push(connectingLine);
                }
            }

            continue; // Skip normal segment creation
        }

        // Add line segment to next fix
        if (i < fixes.length - 1) {
            const nextFix = fixes[i + 1];

            // Skip creating segment if next fix is a DME arc or pturn - they handle their own path creation
            if (nextFix.fix_type === 'dmearc' || nextFix.fix_type === 'pturn') {
                continue;
            }

            let coordinates: [number, number][];

            // Check if this is an arc/hold segment
            if (
                fix.arc_center_latitude !== undefined &&
                fix.arc_center_longitude !== undefined &&
                fix.arc_radius !== undefined &&
                fix.turn_direction !== undefined
            ) {
                // Verify this is actually an arc segment by checking if both points
                // are approximately at the radius distance from the arc center
                const distToStart = calculateDistance(
                    fix.arc_center_latitude,
                    fix.arc_center_longitude,
                    fix.latitude,
                    fix.longitude
                );
                const distToEnd = calculateDistance(
                    fix.arc_center_latitude,
                    fix.arc_center_longitude,
                    nextFix.latitude,
                    nextFix.longitude
                );

                // Allow 10% tolerance for floating point errors
                const tolerance = fix.arc_radius * 0.1;
                const isActualArc =
                    Math.abs(distToStart - fix.arc_radius) < tolerance &&
                    Math.abs(distToEnd - fix.arc_radius) < tolerance;

                if (isActualArc) {
                    // Generate arc
                    coordinates = generateArcPoints(
                        fix.arc_center_latitude,
                        fix.arc_center_longitude,
                        fix.arc_radius,
                        fix.latitude,
                        fix.longitude,
                        nextFix.latitude,
                        nextFix.longitude,
                        fix.turn_direction
                    );
                } else {
                    // Straight segment (e.g., hold pattern legs)
                    coordinates = [
                        [fix.longitude, fix.latitude],
                        [nextFix.longitude, nextFix.latitude],
                    ];
                }
            } else {
                // Direct segment
                coordinates = [
                    [fix.longitude, fix.latitude],
                    [nextFix.longitude, nextFix.latitude],
                ];
            }

            const lineFeature: Feature<LineString> = {
                type: 'Feature',
                geometry: {
                    type: 'LineString',
                    coordinates,
                },
                properties: {
                    type: 'segment',
                    segmentType,
                    identifier,
                    fixType: fix.fix_type,
                    course: fix.course,
                    fromFix: fix.fix_name,
                    toFix: nextFix.fix_name,
                    color: segmentType === 'main' ? '#FF00FF' : '#FF00FF66',
                },
            };
            lineFeatures.push(lineFeature);
        }
    }

    // Return line features first, then point features
    // This ensures points render on top of lines
    return [...lineFeatures, ...pointFeatures];
}
